import React, { useState, useEffect } from "react";
import { useHistory, Link } from "react-router-dom";

export default function UserProfilePage() {
  const history = useHistory();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState([]);
  const [passwordSuccess, setPasswordSuccess] = useState("");

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchEnrolledCourses = async (courseIds = []) => {
    if (!courseIds.length) {
      setEnrolledCourses([]);
      return;
    }

    try {
      const courses = await Promise.all(
        courseIds.map(async (courseId) => {
          try {
            const res = await fetch(`https://iyiliginmucitleri.com/api/courses/${courseId}`);
            if (!res.ok) return null;
            const course = await res.json();
            return course && !course.error ? course : null;
          } catch (err) {
            console.error("Kurs getirilemedi:", courseId, err);
            return null;
          }
        })
      );
      setEnrolledCourses(courses.filter(Boolean));
    } catch (error) {
      console.error("Kayıtlı kurslar alınamadı:", error);
      setEnrolledCourses([]);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        history.push("/login");
        return;
      }

      const response = await fetch('https://iyiliginmucitleri.com/api/user/profile', {
        headers: { 
          'Authorization': `Bearer ${token}`,
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setEditForm(userData);
        await fetchEnrolledCourses(userData.enrolledCourses || []);
      } else if (response.status === 401) {
        localStorage.removeItem("token");
        history.push("/login");
      } else {
        console.error("Profil yüklenemedi");
      }
    } catch (error) {
      console.error("Profil yüklenirken hata:", error);
      // Demo veri
      const demoUser = {
        _id: "123456",
        name: "Ahmet Yılmaz",
        email: "ahmet.yilmaz@email.com",
        role: "student",
        phone: "+90 555 123 4567",
        createdAt: "2024-01-15T10:30:00.000Z",
        bio: "Yazılım geliştirme ve yeni teknolojileri öğrenmekten keyif alıyorum.",
        enrolledCourses: []
      };
      setUser(demoUser);
      setEditForm(demoUser);
      setEnrolledCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch('https://iyiliginmucitleri.com/api/user/profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editForm.name,
          bio: editForm.bio,
          phone: editForm.phone
        })
      });

      if (response.ok) {
        const updatedUser = await response.json();
        setUser(updatedUser);
        setIsEditing(false);
        setSuccessMessage("Profil başarıyla güncellendi!");
        
        setTimeout(() => setSuccessMessage(""), 3000);
      }
    } catch (error) {
      console.error("Güncelleme hatası:", error);
    }
  };

  const handleCancel = () => {
    setEditForm(user);
    setIsEditing(false);
  };

  const handlePasswordChange = (e) => {
    setPasswordForm({
      ...passwordForm,
      [e.target.name]: e.target.value
    });
    setPasswordError([]);
  };

  const handlePasswordSave = async () => {
    setPasswordError([]);
    setPasswordSuccess("");

    // Validasyon
    if (!passwordForm.oldPassword) {
      setPasswordError(["Eski şifrenizi girin"]);
      return;
    }
    if (!passwordForm.newPassword) {
      setPasswordError(["Yeni şifrenizi girin"]);
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError(["Yeni şifreler eşleşmiyor"]);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch('https://iyiliginmucitleri.com/api/user/change-password', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          oldPassword: passwordForm.oldPassword,
          newPassword: passwordForm.newPassword
        })
      });

      if (response.ok) {
        setPasswordForm({
          oldPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setIsChangingPassword(false);
        setPasswordSuccess("Şifre başarıyla değiştirildi!");
        setTimeout(() => setPasswordSuccess(""), 3000);
      } else {
        const errorData = await response.json();
        const errors = errorData.missingRequirements || [errorData.error || errorData.message || "Şifre değiştirilemedi"];
        setPasswordError(Array.isArray(errors) ? errors : [errors]);
      }
    } catch (error) {
      console.error("Şifre değiştirme hatası:", error);
      setPasswordError(["Şifre değiştirirken hata oluştu"]);
    }
  };

  const handlePasswordCancel = () => {
    setPasswordForm({
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setIsChangingPassword(false);
    setPasswordError([]);
  };

  const getPasswordRequirementChecks = (password) => [
    { key: "length", label: "En az 8 karakter", met: password.length >= 8 },
    { key: "maxLength", label: "En fazla 30 karakter", met: password.length <= 30 },
    { key: "uppercase", label: "En az 1 büyük harf", met: /[A-Z]/.test(password) },
    { key: "lowercase", label: "En az 1 küçük harf", met: /[a-z]/.test(password) },
    { key: "special", label: "En az 1 özel karakter", met: /[^a-zA-Z0-9]/.test(password) },
  ];

  const handleChange = (e) => {
    setEditForm({
      ...editForm,
      [e.target.name]: e.target.value
    });
  };

  const getRoleText = (role) => {
    switch(role) {
      case 'admin': return 'Yönetici';
      case 'instructor': return 'Eğitmen';
      default: return 'Öğrenci';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Bilinmiyor";
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Topbar'daki gibi avatar harfi al
  const getAvatarLetter = () => {
    return user?.name?.charAt(0)?.toUpperCase() || "K";
  };

  const passwordRequirementChecks = getPasswordRequirementChecks(passwordForm.newPassword);

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Profil yükleniyor...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={styles.error}>
        <p>Profil yüklenemedi</p>
        <button 
          onClick={() => history.push('/login')}
          style={styles.button}
        >
          Giriş Yap
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Üst Başlık */}
      <div style={styles.header}>
        <button 
          onClick={() => history.goBack()}
          style={styles.backButton}
        >
          ← Geri
        </button>
        <h1 style={styles.title}>Profilim</h1>
      </div>

      {successMessage && (
        <div style={styles.successMessage}>
          {successMessage}
        </div>
      )}

      {passwordSuccess && (
        <div style={styles.successMessage}>
          {passwordSuccess}
        </div>
      )}

      <div style={styles.profileContainer}>
        {/* Sol Taraf - Topbar'daki avatar gibi */}
        <div style={styles.sideSection}>
          <div style={styles.avatarCard}>
            <div style={styles.avatar}>
              {getAvatarLetter()}
            </div>
            <h2 style={styles.userName}>{user.name}</h2>
            <div style={styles.roleBadge}>
              {getRoleText(user.role)}
            </div>
            <p style={styles.email}>{user.email}</p>
            
            {!isEditing && (
              <button 
                onClick={() => setIsEditing(true)}
                style={styles.editButton}
              >
                Profili Düzenle
              </button>
            )}
          </div>

          {/* Üyelik Bilgileri - ID kaldırıldı */}
          <div style={styles.membershipCard}>
            <h3 style={styles.cardTitle}>Üyelik Bilgileri</h3>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Üyelik Tarihi:</span>
              <span style={styles.infoValue}>{formatDate(user.createdAt)}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Rol:</span>
              <span style={styles.infoValue}>{getRoleText(user.role)}</span>
            </div>
          </div>
        </div>

        {/* Sağ Taraf - Detaylı Bilgiler */}
        <div style={styles.mainSection}>
          <div style={styles.detailsCard}>
            <h3 style={styles.cardTitle}>Profil Detayları</h3>
            
            {isEditing ? (
              <div style={styles.editForm}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Ad Soyad</label>
                  <input
                    type="text"
                    name="name"
                    value={editForm.name || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Ad Soyad"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>E-posta</label>
                  <input
                    type="email"
                    value={user.email}
                    style={{...styles.input, backgroundColor: '#f8f9fa'}}
                    disabled
                  />
                  <small style={styles.helperText}>E-posta değiştirilemez</small>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Telefon</label>
                  <input
                    type="tel"
                    name="phone"
                    value={editForm.phone || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Telefon numarası"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Hakkımda</label>
                  <textarea
                    name="bio"
                    value={editForm.bio || ""}
                    onChange={handleChange}
                    style={styles.textarea}
                    placeholder="Kendinizden bahsedin..."
                    rows={4}
                  />
                </div>

                <div style={styles.formActions}>
                  <button 
                    onClick={handleSave}
                    style={styles.saveButton}
                  >
                    Kaydet
                  </button>
                  <button 
                    onClick={handleCancel}
                    style={styles.cancelButton}
                  >
                    İptal
                  </button>
                </div>
              </div>
            ) : (
              <div style={styles.readOnlyInfo}>
                <div style={styles.infoSection}>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Ad Soyad:</span>
                    <span style={styles.infoValue}>{user.name}</span>
                  </div>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>E-posta:</span>
                    <span style={styles.infoValue}>{user.email}</span>
                  </div>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Telefon:</span>
                    <span style={styles.infoValue}>{user.phone || "Belirtilmemiş"}</span>
                  </div>
                </div>

                <div style={styles.coursesSection}>
                  <h4 style={styles.sectionTitle}>Kayıtlı Olduğu Kurslar</h4>
                  {enrolledCourses.length ? (
                    <div style={styles.courseChips}>
                      {enrolledCourses.map((course) => (
                        <Link
                          key={course._id}
                          to={`/course/${course._id}`}
                          style={styles.courseChip}
                        >
                          {course.title || "Kurs"}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <p style={styles.mutedText}>Henüz kurs kaydı yok.</p>
                  )}
                </div>

                <div style={styles.bioSection}>
                  <h4 style={styles.sectionTitle}>Hakkımda</h4>
                  <p style={styles.bioText}>
                    {user.bio || "Henüz bir açıklama eklenmemiş."}
                  </p>
                </div>

                {!isEditing && (
                  <button 
                    onClick={() => setIsEditing(true)}
                    style={styles.editButton}
                  >
                    Profili Düzenle
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Şifre Değiştirme Kartı */}
          <div style={styles.detailsCard}>
            <h3 style={styles.cardTitle}>Güvenlik</h3>
            
            {isChangingPassword ? (
              <div style={styles.editForm}>
                {passwordError.length > 0 && (
                  <div style={styles.errorMessage}>
                    <div style={styles.errorTitle}>Şifre koşulları sağlanmıyor</div>
                    <ul style={styles.errorList}>
                      {passwordError.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div style={styles.formGroup}>
                  <label style={styles.label}>Eski Şifre</label>
                  <input
                    type="password"
                    name="oldPassword"
                    value={passwordForm.oldPassword}
                    onChange={handlePasswordChange}
                    style={styles.input}
                    placeholder="Eski şifrenizi girin"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Yeni Şifre</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    style={styles.input}
                    placeholder="Yeni şifrenizi girin"
                  />
                  <div style={styles.requirementBox}>
                    {passwordRequirementChecks.map((requirement) => (
                      <div
                        key={requirement.key}
                        style={{
                          ...styles.requirementItem,
                          color: requirement.met ? '#2f855a' : '#a94442',
                          borderColor: requirement.met ? '#cde6d4' : '#f1c0c0',
                          backgroundColor: requirement.met ? '#f3f8f4' : '#fff5f5'
                        }}
                      >
                        <span style={styles.requirementIcon}>
                          {requirement.met ? '✓' : '•'}
                        </span>
                        <span>{requirement.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Yeni Şifre Tekrar</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    style={styles.input}
                    placeholder="Yeni şifrenizi tekrar girin"
                  />
                </div>

                <div style={styles.formActions}>
                  <button 
                    onClick={handlePasswordSave}
                    style={styles.saveButton}
                  >
                    Şifre Değiştir
                  </button>
                  <button 
                    onClick={handlePasswordCancel}
                    style={styles.cancelButton}
                  >
                    İptal
                  </button>
                </div>
              </div>
            ) : (
              <div style={styles.readOnlyInfo}>
                <p style={styles.securityInfo}>
                  Hesabınızı korumak için şifrenizi düzenli olarak değiştirmeniz önerilir.
                </p>
                <button 
                  onClick={() => setIsChangingPassword(true)}
                  style={styles.changePasswordButton}
                >
                  Şifre Değiştir
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '32px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    background: '#f7f7f7',
    minHeight: '100vh',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    background: '#f7f7f7',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #f3f3f3',
    borderTop: '3px solid #42a947',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '20px',
    color: '#333',
    fontSize: '14px',
    fontWeight: '500',
  },
  error: {
    textAlign: 'center',
    padding: '100px 20px',
  },
  button: {
    padding: '8px 20px',
    background: 'transparent',
    color: '#333',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    marginTop: '20px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '32px',
    paddingBottom: '16px',
    borderBottom: '1px solid #eee',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#333',
    margin: '0',
    flex: 1,
    textAlign: 'center',
  },
  backButton: {
    padding: '8px 16px',
    background: 'transparent',
    color: '#666',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  successMessage: {
    backgroundColor: '#d4edda',
    color: '#155724',
    padding: '12px 20px',
    borderRadius: '8px',
    marginBottom: '24px',
    border: '1px solid #c3e6cb',
    fontSize: '14px',
  },
  profileContainer: {
    display: 'grid',
    gridTemplateColumns: '300px 1fr',
    gap: '32px',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  sideSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  avatarCard: {
    background: '#fff',
    padding: '32px',
    borderRadius: '12px',
    border: '1px solid #eee',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    textAlign: 'center',
  },
  avatar: {
    width: '100px', // Topbar'dakinden büyük (32px yerine 100px)
    height: '100px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #42a947ff 0%, #84982cff 100%)',
    color: 'white',
    fontSize: '40px', // Font büyüklüğü arttı
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    // Topbar'daki N harfi stilinde:
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
  userName: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#333',
    margin: '0 0 8px',
  },
  roleBadge: {
    display: 'inline-block',
    background: '#f8f9fa',
    color: '#333',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '500',
    marginBottom: '16px',
    border: '1px solid #e0e0e0',
  },
  email: {
    color: '#666',
    fontSize: '14px',
    margin: '0 0 24px',
    fontWeight: '500',
  },
  membershipCard: {
    background: '#fff',
    padding: '24px',
    borderRadius: '12px',
    border: '1px solid #eee',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
  },
  mainSection: {
    background: '#fff',
    padding: '32px',
    borderRadius: '12px',
    border: '1px solid #eee',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  detailsCard: {
    background: '#fff',
    borderRadius: '12px',
    border: '1px solid #eee',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#333',
    margin: '0 0 24px',
    paddingBottom: '16px',
    borderBottom: '1px solid #eee',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '16px',
    paddingBottom: '16px',
    borderBottom: '1px solid #f8f9fa',
  },
  infoLabel: {
    color: '#666',
    fontSize: '14px',
    fontWeight: '500',
  },
  infoValue: {
    color: '#333',
    fontSize: '14px',
    fontWeight: '400',
  },
  editForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
  },
  input: {
    padding: '10px 12px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    transition: 'all 0.2s',
    background: '#f8f9fa',
  },
  textarea: {
    padding: '10px 12px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
    resize: 'vertical',
    minHeight: '100px',
    background: '#f8f9fa',
  },
  helperText: {
    fontSize: '12px',
    color: '#666',
    marginTop: '4px',
  },
  formActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '20px',
  },
  saveButton: {
    padding: '10px 20px',
    background: 'transparent',
    color: '#333',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    flex: 1,
    transition: 'all 0.2s ease',
  },
  cancelButton: {
    padding: '10px 20px',
    background: 'transparent',
    color: '#666',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    flex: 1,
    transition: 'all 0.2s ease',
  },
  readOnlyInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  infoSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  bioSection: {
    paddingTop: '20px',
    borderTop: '1px solid #eee',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    margin: '0 0 12px',
  },
  bioText: {
    color: '#555',
    fontSize: '14px',
    lineHeight: '1.6',
    margin: '0',
    fontWeight: '400',
  },
  coursesSection: {
    paddingTop: '8px',
    borderTop: '1px solid #eee',
  },
  courseChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginTop: '10px',
  },
  courseChip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '8px 12px',
    background: '#f3f6f4',
    color: '#2f855a',
    borderRadius: '999px',
    textDecoration: 'none',
    fontSize: '13px',
    fontWeight: '600',
    border: '1px solid #e0e6e2',
    transition: 'all 0.2s ease',
  },
  mutedText: {
    color: '#777',
    fontSize: '13px',
    margin: '6px 0 0',
  },
  errorMessage: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    padding: '12px 20px',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '1px solid #f5c6cb',
    fontSize: '14px',
  },
  errorTitle: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '8px',
  },
  errorList: {
    margin: 0,
    paddingLeft: '18px',
    display: 'grid',
    gap: '4px',
  },
  securityInfo: {
    color: '#666',
    fontSize: '14px',
    lineHeight: '1.6',
    margin: '0 0 20px',
    fontWeight: '400',
  },
  changePasswordButton: {
    width: '100%',
    padding: '10px 20px',
    background: 'transparent',
    color: '#666',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  requirementBox: {
    display: 'grid',
    gap: '8px',
    marginTop: '10px',
  },
  requirementItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 10px',
    borderRadius: '8px',
    border: '1px solid',
    fontSize: '13px',
    fontWeight: '500',
  },
  requirementIcon: {
    width: '16px',
    flexShrink: 0,
    textAlign: 'center',
  },
  editButton: {
    width: '100%',
    padding: '10px 20px',
    background: 'transparent',
    color: '#666',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    marginTop: '20px',
  },
};

// CSS Animasyonu ve hover efektleri
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  input:focus, textarea:focus {
    border-color: #42a947;
    background: #fff;
  }
  
  .editButton:hover, .saveButton:hover, .cancelButton:hover {
    background: #f8f9fa;
    border-color: #ced4da;
  }
  
  .backButton:hover {
    background: #f8f9fa;
  }
`;
document.head.appendChild(styleSheet);
